import type { TaxonDto } from '@/api/TaxonomyApi/generated/model/taxonDto';
import { useTaxonsGetTaxons } from '@/api/TaxonomyApi/generated/taxons/taxons';
import { EmptyStateView } from '@/components/EmptyStateView/EmptyStateView';
import { Input } from '@/components/Input/Input';
import { ScreenHeader } from '@/components/ScreenHeader';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useDebounce } from '@/hooks/useDebounce';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineDegradedMode } from '@/hooks/useOfflineDegradedMode';
import { useSpeciesSelectionStore } from '@/store/speciesSelection/speciesSelection.store';
import { Button } from '@components/Button/Button';
import { LoadingView } from '@components/LoadingView/LoadingView';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import { useState } from 'react';
import { ScrollView, SizableText, XStack, YStack, useTheme } from 'tamagui';
import { AddFileModal } from './components/AddFileModal';
import { SpeciesSearchBackAction } from './components/SpeciesSearchBackAction';
import type { SpeciesSearchResultItem } from './components/SpeciesSearchResults';
import { SpeciesSearchResults } from './components/SpeciesSearchResults';
import { useSpeciesFilePicker } from './hooks/useSpeciesFilePicker/useSpeciesFilePicker';
import type { SelectedFile } from './hooks/useSpeciesFilePicker/useSpeciesFilePicker.types';
import {
  buildAddFileModalOptions,
  VERTEBRATE_TAXON,
} from './speciesSearch.constants';
import { CreateReportDtoAnimalType } from '@api/WhisApi/generated';
import { SpeciesTaxonomyQueryParams } from '@screens/SpeciesSearch/SpeciesTaxonomyScreen/speciesTaxonomy.types';

export const SpeciesSearchScreen = (): React.ReactElement => {
  const { t, language } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const { animalType } = useLocalSearchParams<{
    animalType?: string;
  }>();
  const theme = useTheme();
  const router = useRouter();
  const { setSelection } = useSpeciesSelectionStore();
  const segments = useSegments() as string[];
  const { currentValue, reportId } = useLocalSearchParams<{
    currentValue?: string;
    reportId?: string;
  }>();
  const [query, setQuery] = useState('');
  const [offlineSpeciesInput, setOfflineSpeciesInput] = useState(
    typeof currentValue === 'string' ? currentValue : '',
  );
  const debouncedQuery = useDebounce(query.trim(), 300);
  const [addFileModalVisible, setAddFileModalVisible] = useState(false);
  const { isOfflineDegradedMode } = useOfflineDegradedMode();
  const { isOnline, isNetworkStatusPending } = useNetworkStatus();
  const isOfflineMode = isOfflineDegradedMode || !isOnline;

  const normalizedQuery = debouncedQuery.toLowerCase();

  const taxonsQueryParams = { search: normalizedQuery || '' };

  const {
    data: taxonsResponse,
    isFetching,
    isPending,
  } = useTaxonsGetTaxons(taxonsQueryParams, {
    query: {
      enabled: Boolean(normalizedQuery),
      staleTime: 6 * 60 * 60_000, // Setting stale time to 6 hours
      gcTime: Infinity, // Do not delete data if no fresh data available
    },
  });

  const taxons = taxonsResponse?.taxons ?? [];
  const resultItems = taxons.map((taxon: TaxonDto) => {
    const localizedName = taxon.vernacularNames.find(
      (item) => item.language === language,
    )?.name;
    const label =
      localizedName ??
      taxon.vernacularNames[0]?.name ??
      taxon.scientificName ??
      taxon.genus ??
      taxon.code;
    return {
      value: taxon.code,
      scientificName: taxon.scientificName ?? taxon.genus ?? taxon.code,
      label,
      subtitle: taxon.scientificName ?? taxon.genus ?? '',
      isSelected:
        typeof currentValue === 'string' && currentValue === taxon.code,
    };
  });

  const isEditRoute = Boolean(
    reportId && segments.includes('reports') && segments.includes('edit'),
  );
  const isBoardRoute = segments.includes('board');

  const navigateToSpeciesRoute = (segment: 'ai' | 'taxonomy'): void => {
    const queryParams: SpeciesTaxonomyQueryParams = {};

    if (animalType === CreateReportDtoAnimalType.vertebrate) {
      queryParams.rootTaxon = VERTEBRATE_TAXON;
    }
    if (isEditRoute && reportId) {
      const editBasePath = isBoardRoute
        ? '/(tabs)/board/reports/[reportId]/edit/species'
        : '/(tabs)/reports/[reportId]/edit/species';

      router.push({
        pathname: `${editBasePath}/${segment}`,
        params: { ...queryParams, reportId },
      });
      return;
    }
    router.push({
      pathname: `/(tabs)/report-creation/species/${segment}`,
      params: queryParams,
    });
  };

  const handleFileSelected = (file: SelectedFile): void => {
    const params: Record<string, string> = {
      fileUri: file.uri,
    };

    if (file.fileName) {
      params.fileName = file.fileName;
    }

    if (file.fileType) {
      params.fileType = file.fileType;
    }

    if (isEditRoute && reportId) {
      const editAiPath = isBoardRoute
        ? '/(tabs)/board/reports/[reportId]/edit/species/ai'
        : '/(tabs)/reports/[reportId]/edit/species/ai';

      router.push({
        pathname: editAiPath,
        params: { reportId, ...params },
      });
      return;
    }

    router.push({
      pathname: '/(tabs)/report-creation/species/ai',
      params,
    });
  };

  const {
    cameraAvailable,
    pickFromLibrary,
    takePhoto,
    chooseFile,
    shouldOpenFilePickerDirectly,
  } = useSpeciesFilePicker({ onSelect: handleFileSelected });

  const handleSelect = (item: SpeciesSearchResultItem): void => {
    setSelection({
      value: item.scientificName,
      label: item.label,
    });
    router.back();
  };

  const handleOfflineSpeciesSubmit = (): void => {
    const trimmedValue = offlineSpeciesInput.trim();
    if (!trimmedValue) {
      return;
    }

    setSelection({
      value: trimmedValue,
      label: trimmedValue,
    });
    router.back();
  };

  const closeAddFileModal = (): void => {
    setAddFileModalVisible(false);
  };

  const modalOptions = shouldOpenFilePickerDirectly
    ? []
    : buildAddFileModalOptions({
        t,
        cameraAvailable,
        onClose: closeAddFileModal,
        pickFromLibrary,
        takePhoto,
        chooseFile,
      });

  const handleAiIdentifyPress = (): void => {
    if (shouldOpenFilePickerDirectly) {
      void chooseFile();
      return;
    }

    setAddFileModalVisible(true);
  };

  const displayResult = (): React.ReactNode => {
    if (isPending && isFetching) {
      return <LoadingView />;
    }

    if (!normalizedQuery) {
      return (
        <EmptyStateView
          title={t('speciesSearch.empty.promptTitle')}
          description={t('speciesSearch.empty.promptDescription')}
        />
      );
    }

    return (
      <YStack gap="$2" flex={1}>
        <SizableText size="$2" fontFamily="$bold">
          {t('speciesSearch.results.title')}
        </SizableText>
        <ScrollView showsVerticalScrollIndicator={false} flex={1}>
          <SpeciesSearchResults items={resultItems} onSelect={handleSelect} />
        </ScrollView>
      </YStack>
    );
  };

  if (isNetworkStatusPending && !isOfflineDegradedMode) {
    return <LoadingView />;
  }

  return (
    <TabScreenContainer>
      {!isSmallScreen && (
        <ScreenHeader
          title={t('speciesSearch.title')}
          actions={<SpeciesSearchBackAction />}
        />
      )}

      <YStack gap="$3" flex={1}>
        {isOfflineMode ? (
          <YStack gap="$3">
            <SizableText size="$2" color="$secondary50">
              {t('offlineMode.banner')}
            </SizableText>
            <Input
              value={offlineSpeciesInput}
              onChangeText={setOfflineSpeciesInput}
              placeholder={t(
                'reportCreation.step1.placeholders.speciesFreeText',
              )}
              autoCapitalize="none"
            />
            <Button
              title={t('buttons.validate')}
              onPress={handleOfflineSpeciesSubmit}
              disabled={!offlineSpeciesInput.trim()}
            />
          </YStack>
        ) : (
          <>
            <Input
              value={query}
              onChangeText={setQuery}
              placeholder={t('speciesSearch.searchPlaceholder')}
              autoCapitalize="none"
            />

            <XStack gap="$2">
              <Button
                variant="outlined"
                title={t('speciesSearch.actions.taxonomyTree')}
                icon={
                  <Ionicons
                    name="git-branch-outline"
                    size={18}
                    color={theme.color?.val}
                  />
                }
                flex={1}
                onPress={() => navigateToSpeciesRoute('taxonomy')}
              />
              <Button
                title={t('speciesSearch.actions.identifyWithAi')}
                icon={
                  <Ionicons
                    name="camera-outline"
                    size={18}
                    color={theme.background?.val}
                  />
                }
                flex={1}
                onPress={handleAiIdentifyPress}
              />
            </XStack>

            {displayResult()}
          </>
        )}
      </YStack>

      {!isOfflineMode && !shouldOpenFilePickerDirectly && (
        <AddFileModal
          visible={addFileModalVisible}
          options={modalOptions}
          onClose={closeAddFileModal}
        />
      )}
    </TabScreenContainer>
  );
};
